// AnalysisJob — mirrors backend scan.models.AnalysisJob
export type JobStatus = 'CREATED' | 'QUEUED' | 'PROVISIONING' | 'INITIALIZING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export interface AnalysisJob {
    id: string;
    status: JobStatus;
    user: number;
    initiated_by_username: string;
    initiated_by_email: string;
    input_data_uri: string;
    input_data_hash: string | null;
    result_uri: string | null;
    pipeline_version: string;
    model_version: string;
    code_version: string;
    parameters: Record<string, unknown>;
    error_message: string | null;
    created_at: string;
    started_at: string | null;
    completed_at: string | null;
    /** True per-job scan acquisition time (parsed from HDF5 record_time). Set
     *  by the pipeline for new uploads, and by a one-time backfill for
     *  historical jobs (sourced from PatientFileMapping or, for single-scan
     *  patients only, Patient.last_visit). Null when no reliable per-job
     *  evidence exists. */
    scan_time: string | null;
    reviewed: boolean;
    reviewed_by: number | null;
    reviewed_by_username: string | null;
    reviewed_at: string | null;
    last_heartbeat_at: string | null;
    parent_job_id: string | null;
    /** @deprecated use patient_id */
    patient: number | null;
    /** Canonical patient FK id; null when unlinked */
    patient_id?: number | null;
    /** Bare label from API — UI adds Subject: / Session: */
    patient_label?: string | null;
    patient_is_session?: boolean | null;
    /** @deprecated use patient_label */
    patient_name: string | null;
    notes: string | null;
}

/** Scan needs linking to a real subject (no patient or session placeholder). */
export function jobNeedsSubjectLink(job: AnalysisJob): boolean {
    const pid = job.patient_id ?? job.patient;
    if (pid == null) return true;
    if (job.patient_is_session === true) return true;
    if (job.patient_is_session === false) return false;
    // Conservative fallback for legacy payloads: linked patient defaults to subject-linked.
    return false;
}

/** Scan is linked to a real subject (reassignment path). */
export function jobIsSubjectLinked(job: AnalysisJob): boolean {
    const pid = job.patient_id ?? job.patient;
    if (pid == null) return false;
    if (job.patient_is_session === true) return false;
    // Conservative fallback for legacy payloads with linked patient.
    return true;
}

export interface JobSummary {
    queued: number;
    processing: number;
    failed: number;
    completed_24h: number;
    needs_review: number;
    stuck: number;
}

// Sub-scan result payload (v2)
export interface SubScanAsset {
    url: string;
}

export interface ParsedData<T> {
    url: string;
    data: T | null;
    error: string | null;
}

export interface StatsSummaryData {
    saccade_count: number;
    saccade_frequency_hz: number;
    saccade_average_amplitude_deg: number;
    saccade_average_peak_velocity_degs: number;
    drift_velocity_degs: number;
    drift_ratio: number;
    blink_count: number;
    directional_deviation: number;
}

export interface SaccadeEntry {
    index: number;
    start_time_s: number;
    end_time_s: number;
    speed_degs: number;
    amp_deg: number;
}

export interface PolarData {
    phis_deg: number[];
    rs_deg: number[];
    /** Bivariate contour ellipse area (deg²), fixation stability only when present */
    area_deg2?: number;
    angle_reference: string;
    error: string | null;
}

export interface NpyMetrics {
    microsaccade_frequency_hz: number;
    microsaccade_amplitude_deg: number;
    microsaccade_peak_velocity_degs: number;
    drift_velocity_degs: number;
    drift_ratio: number;
    blink_count: number;
    directional_deviation_deg: number;
    error: string | null;
}

export interface SubScanResult {
    key: string;
    mode: number;
    odos: number;
    eye: string;
    scan: number;
    label: string;
    review_screen?: SubScanAsset;
    gif?: SubScanAsset;
    stats_summary?: ParsedData<StatsSummaryData>;
    saccade_summary?: ParsedData<SaccadeEntry[]>;
    trace?: SubScanAsset;
    annotated_video?: SubScanAsset;
    raw_video?: SubScanAsset;
    fixation_stability_polar?: PolarData;
    microsaccade_amplitude_polar?: PolarData;
    npy_metrics?: NpyMetrics;
}

export interface JobResult {
    schema_version: string;
    job_id: string;
    scan_id: string;
    scan_uri: string;
    status: string;
    sub_scans: SubScanResult[];
    total_summary?: SubScanAsset;
    report_pdf?: SubScanAsset | null;
    files: { filename: string; url: string }[];
    quality_flags: string[];
}

// UI-level display status — one vocabulary for all pages
export type DisplayStatus =
    | 'Queued'
    | 'Processing'
    | 'Completed'
    | 'Failed'
    | 'Needs Review'
    | 'Stuck';

/**
 * Map backend AnalysisJob status to a single UI display label.
 * RUNNING / CREATED / PROVISIONING / INITIALIZING all become "Processing".
 * SUCCEEDED becomes "Completed" or "Needs Review" based on reviewed flag.
 * Never render raw backend statuses in UI text — always use this function.
 */
export function toDisplayStatus(job: AnalysisJob, stuckThresholdMs = 10 * 60 * 1000): DisplayStatus {
    if (job.status === 'FAILED') return 'Failed';
    if (job.status === 'QUEUED') return 'Queued';

    if (job.status === 'SUCCEEDED') {
        return job.reviewed ? 'Completed' : 'Needs Review';
    }

    // RUNNING, CREATED, PROVISIONING, INITIALIZING → Processing (with stuck detection)
    if (
        job.status === 'RUNNING' &&
        job.last_heartbeat_at &&
        Date.now() - new Date(job.last_heartbeat_at).getTime() > stuckThresholdMs
    ) {
        return 'Stuck';
    }

    return 'Processing';
}

// Patient aggregate status — priority rule from plan
export type PatientPipelineStatus = 'Failed' | 'Processing' | 'Queued' | 'Needs Review' | 'Completed';

export function patientAggregateStatus(jobs: AnalysisJob[]): PatientPipelineStatus {
    if (jobs.length === 0) return 'Completed';

    if (jobs.some(j => j.status === 'FAILED')) return 'Failed';
    if (jobs.some(j => ['RUNNING', 'CREATED', 'PROVISIONING', 'INITIALIZING'].includes(j.status))) return 'Processing';
    if (jobs.some(j => j.status === 'QUEUED')) return 'Queued';
    if (jobs.some(j => j.status === 'SUCCEEDED' && !j.reviewed)) return 'Needs Review';
    return 'Completed';
}
